/**
 * The errors this package throws.
 *
 * Every one of them is a programming or state error: an unknown marker id, an overlay slug
 * the manifest does not carry, a specification name that names nothing, a lock asked for by a
 * user the host says may not edit. None of them is a wire error: this package never calls
 * `fetch`, so a network failure always arrives as the client's own taxonomy and passes through
 * here untouched. That is why these classes carry no status and no envelope code: there is no
 * response behind them to report.
 *
 * Catching `MountayaElementsError` separates a misuse of this package from every other
 * failure in the calling program, which is the whole reason to have a family rather than
 * bare `Error`s: `instanceof` is the one programmatic handle a thrown value offers.
 */

import { printValue } from "./print.js";

/**
 * MountayaElementsError is the base of every error this package throws.
 *
 * Each class declares its `name` as a string LITERAL, so a subclass reports itself: a stack
 * trace and a test failure both lead with the most specific class, not with the base that
 * holds the constructor. A name read off `new.target` gives the same string only until a
 * consumer minifies: a class name is an identifier, every production bundler renames
 * identifiers by default, and a check that passes in their development build then meets
 * `name: "a"`. A literal is data and survives.
 */
export class MountayaElementsError extends Error {
  override readonly name: string = "MountayaElementsError";

  // The constructor narrows the signature rather than adding behaviour: `Error`'s `message`
  // is optional and this family's is not. Dropping it makes
  // `new MountayaElementsNotFoundError()` compile, which is a thrown value carrying nothing
  // a caller can read.
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
  }
}

/**
 * MountayaElementsNotFoundError is thrown when a name or id is resolved against what the map
 * holds and nothing answers: a marker or analysis id that was never added or already
 * removed, an overlay or terrain slug the layers manifest does not list.
 *
 * It is the one failure a caller routinely handles rather than fixes: an id can go stale
 * through a legitimate removal elsewhere, so "gone" is worth narrowing on where the other
 * misuses are simply bugs.
 */
export class MountayaElementsNotFoundError extends MountayaElementsError {
  override readonly name = "MountayaElementsNotFoundError";
}

/**
 * MountayaElementsForbiddenError is thrown when the host's own answer refuses the local user
 * what was asked, before anything reaches the API: a collaboration lock asked for while
 * `CollabEngineConfig.canEdit` answers that the user holds no write role on the resource.
 *
 * The API refuses the same request with a 403, and this is the refusal it stands in for: the
 * user may not do this, and asking again meets the same answer until their role changes. So a
 * caller reports it as it reports that 403, with the words it has for a refused permission. It
 * carries no status, as no class of this family does, because nothing answered it.
 *
 * @example
 * ```ts
 * import type { CollabEngine } from "@mountaya/elements/collab";
 * import { MountayaElementsForbiddenError } from "@mountaya/elements/errors";
 *
 * declare const session: CollabEngine;
 * declare const explainRole: () => void;
 *
 * session.acquireLock({ id: "segment-id", kind: "segment" }).catch((error: unknown) => {
 *   if (error instanceof MountayaElementsForbiddenError) {
 *     explainRole();
 *   }
 * });
 * ```
 */
export class MountayaElementsForbiddenError extends MountayaElementsError {
  override readonly name = "MountayaElementsForbiddenError";
}

/**
 * MountayaElementsRejectionError is the error a handler of this family is handed in place of a
 * value that is no `Error`: what a seam, a transport or a hook of the host's own rejected or
 * threw with. That value is its `cause`, as it was caught.
 *
 * JavaScript lets a program throw anything, and a handler typed `(error: Error) => void` is a
 * promise that it is handed an `Error`. Every `onError` and every `error` this family publishes
 * keeps it through {@link errorFrom}: an `Error` arrives as it was thrown, and anything else
 * arrives as one of these, so `instanceof MountayaElementsRejectionError` tells the two apart
 * and `cause` holds what the host's own code produced.
 *
 * @example
 * ```ts
 * import { MountayaElementsRejectionError } from "@mountaya/elements/errors";
 *
 * declare const report: (problem: unknown) => void;
 *
 * const onError = (error: Error): void => {
 *   // A seam of your own rejected with something that is no Error: report that value.
 *   report(error instanceof MountayaElementsRejectionError ? error.cause : error);
 * };
 * ```
 */
export class MountayaElementsRejectionError extends MountayaElementsError {
  override readonly name = "MountayaElementsRejectionError";
}

/**
 * isError tells an `Error` from every other value, one of another realm included: an error
 * thrown inside a frame or a worker is no instance of this realm's `Error`, and is an `Error`
 * all the same. A value that throws as it is read, a revoked proxy, is none.
 */
const isError = (caught: unknown): caught is Error => {
  try {
    return caught instanceof Error || Object.prototype.toString.call(caught) === "[object Error]";
  } catch {
    return false;
  }
};

/**
 * errorFrom answers the `Error` a handler is handed for something caught: the value itself when
 * it is an `Error`, and a {@link MountayaElementsRejectionError} holding it as its `cause`
 * otherwise. It never throws.
 *
 * It is the one place this family turns a caught value into the `Error` it reports, so a handler
 * typed `(error: Error) => void` is handed an `Error` whatever a seam of the host's own rejected
 * with, a value no string can be made of included.
 *
 * @param caught What was thrown, or what a promise rejected with.
 * @param failure The sentence the wrap's message opens with: what failed, and what to check.
 *   Read only where `caught` is no `Error`.
 * @returns `caught` itself when it is an `Error`. Otherwise a
 *   {@link MountayaElementsRejectionError} whose message is `failure` followed by the value as
 *   this package prints one, and whose `cause` is `caught`.
 *
 * @example
 * ```ts
 * import { errorFrom } from "@mountaya/elements/errors";
 *
 * declare const locate: () => Promise<[number, number]>;
 * declare const onError: (error: Error) => void;
 *
 * locate().catch((caught: unknown) => {
 *   onError(errorFrom(caught, "The device location request failed. Check the geolocation seam."));
 * });
 * ```
 */
export const errorFrom = (caught: unknown, failure: string): Error =>
  isError(caught)
    ? caught
    : new MountayaElementsRejectionError(
        `${failure} It rejected or threw with ${printValue(caught)}, which is no Error and is ` +
          "this error's cause.",
        { cause: caught },
      );
